import { apiClient } from './api';
import { Plans, TenantStatus } from '@erpgold/shared';

// API response/payload field snake_case (ADR-013 §D2/§D4)
//
// BO platform can receive 'pending' in addition to shared TenantStatus values.
// 'pending' is platform-admin specific lifecycle state before tenant activation.
export type BoTenantStatus = TenantStatus | 'pending';
export type BoPlanType = Plans;

export interface Tenant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  plan: BoPlanType;
  status: BoTenantStatus;
  store_count: number;
  user_count: number;
  created_at: string;
  updated_at: string;
  last_active_at?: string;
  expires_at?: string;
  billing_info?: {
    address: string;
    tax_code?: string;
  };
}

export interface CreateTenantDto {
  name: string;
  email: string;
  phone?: string;
  plan: string;
  password: string;
}

export interface UpdateTenantDto {
  name?: string;
  email?: string;
  phone?: string;
  plan?: string;
  status?: string;
}

export interface TenantFilters {
  status?: string;
  plan_id?: string;
  plan?: string;
  search?: string;
  created_after?: string;
  created_before?: string;
  sort_by?: 'created_at' | 'name' | 'last_active_at';
  sort_dir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface TenantsResponse {
  data: Tenant[];
  meta: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

interface TenantApiRaw {
  id?: string;
  _id?: string;
  name?: string;
  email?: string;
  phone?: string;
  plan?: string;
  plan_name?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  trial_ends_at?: string;
  stats?: {
    total_stores?: number;
    total_users?: number;
  };
  last_active_at?: string;
}

interface LifecycleResponse {
  tenant_id: string;
  previous_status: string;
  current_status: string;
  suspended_at?: string;
  reason?: string | null;
}

export interface ImpersonateTenantResponse {
  impersonation_token: string;
  tenant_id: string;
  redirect_url: string;
  expires_in: number;
  warning: string;
}

export interface ExtendTrialResponse {
  tenant_id: string;
  previous_trial_ends_at: string;
  new_trial_ends_at: string;
  extended_days: number;
}

export interface ChangePlanResponse {
  tenant_id: string;
  previous_plan: string;
  new_plan: string;
  changed_at: string;
  effective_date: string;
}

export interface AuditLogEntry {
  id: string;
  admin_id: string;
  tenant_id: string;
  action: string;
  ip_address: string;
  user_agent: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface AuditLogsResponse {
  items: AuditLogEntry[];
  meta: {
    total: number;
    limit: number;
    page: number;
    total_pages: number;
  };
}

export interface MisaSyncTriggerResponse {
  success: boolean;
  message: string;
}

export interface TenantStaffMember {
  _id: string;
  name: string;
  email: string;
  username?: string;
  role?: string;
  state?: number;
  store_id?: string;
  created_at?: string;
}

export interface TenantStore {
  _id: string;
  name: string;
  code?: string;
  address?: string;
  full_address?: string;
  store_type?: number;
  state?: number;
  created_at?: string;
}

export interface PaginatedListResponse<T> {
  items: T[];
  meta: {
    total: number;
    limit: number;
    page: number;
    total_pages: number;
  };
}

const toTenant = (raw: TenantApiRaw): Tenant => ({
  id: raw.id || raw._id || '',
  name: raw.name || 'N/A',
  email: raw.email || '',
  phone: raw.phone,
  plan: (raw.plan || raw.plan_name || Plans.Starter) as BoPlanType,
  status: (raw.status || 'pending') as BoTenantStatus,
  store_count: raw.stats?.total_stores || 0,
  user_count: raw.stats?.total_users || 0,
  created_at: raw.created_at || new Date().toISOString(),
  updated_at: raw.updated_at || raw.created_at || new Date().toISOString(),
  last_active_at: raw.last_active_at,
  expires_at: raw.trial_ends_at,
});

const unwrap = <T>(payload: Envelope<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as Envelope<T>).data;
  }
  return payload as T;
};

export const tenantService = {
  getTenants: async (filters: TenantFilters = {}): Promise<TenantsResponse> => {
    const normalizedFilters = {
      ...filters,
      ...(filters.plan_id ? {} : filters.plan ? { plan_id: filters.plan } : {}),
    };

    // BE shape (PaginatedResult + TransformResponseInterceptor):
    //   { success, data: T[], meta: { page, limit, total, total_pages, ... } }
    // Trước đây code đọc nested `data.data.items` + `data.data.meta` — sai
    // shape → bảng rỗng dù API trả 9 tenant. Đọc thẳng `data.data` (array)
    // và `data.meta` (top-level).
    const response = await apiClient.get<
      Envelope<TenantApiRaw[]> & { meta?: TenantsResponse['meta'] & { totalPages?: number } }
    >('/admin/tenants', { params: normalizedFilters });
    const items = Array.isArray(response.data?.data) ? response.data.data : [];
    const payloadMeta = response.data?.meta;

    return {
      data: items.map(toTenant),
      meta: payloadMeta
        ? {
          page: payloadMeta.page,
          limit: payloadMeta.limit,
          total: payloadMeta.total,
          total_pages: payloadMeta.total_pages ?? payloadMeta.totalPages ?? 0,
        }
        : {
          page: filters.page || 1,
          limit: filters.limit || 20,
          total: 0,
          total_pages: 0,
        },
    };
  },

  getTenantById: async (id: string): Promise<Tenant> => {
    const response = await apiClient.get<Envelope<TenantApiRaw>>(`/tenants/${id}`);
    return toTenant(response.data.data);
  },

  createTenant: async (data: CreateTenantDto): Promise<Tenant> => {
    const response = await apiClient.post<Envelope<TenantApiRaw> | TenantApiRaw>('/tenants', data);
    return toTenant(unwrap(response.data));
  },

  updateTenant: async (id: string, data: UpdateTenantDto): Promise<Tenant> => {
    const response = await apiClient.put<Envelope<TenantApiRaw> | TenantApiRaw>(`/tenants/${id}`, data);
    return toTenant(unwrap(response.data));
  },

  suspendTenant: async (id: string, reason: string): Promise<LifecycleResponse> => {
    const response = await apiClient.put<Envelope<LifecycleResponse>>(`/tenants/${id}/suspend`, { reason });
    return response.data.data;
  },

  activateTenant: async (id: string): Promise<LifecycleResponse> => {
    const response = await apiClient.put<Envelope<LifecycleResponse>>(`/tenants/${id}/activate`);
    return response.data.data;
  },

  extendTrial: async (id: string, days: number): Promise<ExtendTrialResponse> => {
    const response = await apiClient.put<Envelope<ExtendTrialResponse>>(`/tenants/${id}/extend-trial`, { days });
    return response.data.data;
  },

  changePlan: async (id: string, planId: string): Promise<ChangePlanResponse> => {
    const response = await apiClient.put<Envelope<ChangePlanResponse>>(`/tenants/${id}/change-plan`, { plan_id: planId });
    return response.data.data;
  },

  impersonateTenant: async (id: string): Promise<ImpersonateTenantResponse> => {
    const response = await apiClient.post<Envelope<ImpersonateTenantResponse>>(`/tenants/${id}/impersonate`);
    return response.data.data;
  },

  deleteTenant: async (id: string): Promise<void> => {
    await apiClient.delete(`/tenants/${id}`);
  },

  getTenantStats: async (id: string): Promise<{
    revenue: number;
    orders: number;
    users: number;
    stores: number;
    products: number;
    storage_used: number;
    last_login_at?: string;
    last_order_at?: string;
    last_activity: string;
  }> => {
    const response = await apiClient.get(`/tenants/${id}/stats`);
    const payload = response.data?.data ?? {};
    return {
      revenue: payload.revenue ?? payload.total_revenue ?? 0,
      orders: payload.orders ?? payload.total_orders ?? 0,
      users: payload.users ?? payload.total_users ?? 0,
      stores: payload.stores ?? payload.total_stores ?? 0,
      products: payload.products ?? payload.total_products ?? 0,
      storage_used: payload.storage_used ?? 0,
      last_login_at: payload.last_login_at,
      last_order_at: payload.last_order_at,
      last_activity:
        payload.last_activity ??
        payload.last_login_at ??
        payload.last_order_at ??
        '',
    };
  },

  getAuditLogs: async (
    tenantId: string,
    filters: { action?: string; from_date?: string; to_date?: string; limit?: number; page?: number } = {},
  ): Promise<AuditLogsResponse> => {
    const response = await apiClient.get<Envelope<{ items: AuditLogEntry[]; meta: AuditLogsResponse['meta'] & { totalPages?: number } }>>(
      `/tenants/${tenantId}/audit-logs`,
      { params: filters },
    );
    const payloadMeta = response.data.data?.meta;

    return {
      items: response.data.data?.items || [],
      meta: payloadMeta
        ? {
          total: payloadMeta.total,
          limit: payloadMeta.limit,
          page: payloadMeta.page,
          total_pages: payloadMeta.total_pages ?? payloadMeta.totalPages ?? 0,
        }
        : { total: 0, limit: 50, page: 1, total_pages: 0 },
    };
  },

  triggerMisaSync: async (tenantId: string): Promise<MisaSyncTriggerResponse> => {
    const response = await apiClient.post<Envelope<MisaSyncTriggerResponse>>(`/tenants/${tenantId}/misa-sync`);
    return response.data.data;
  },

  getTenantStaff: async (
    tenantId: string,
    filters: { page?: number; limit?: number; q?: string } = {},
  ): Promise<PaginatedListResponse<TenantStaffMember>> => {
    const response = await apiClient.get<{ success: boolean; data: TenantStaffMember[]; meta: PaginatedListResponse<TenantStaffMember>['meta'] }>(
      `/tenants/${tenantId}/staff`,
      { params: filters },
    );
    const { data, meta } = response.data;
    return {
      items: data || [],
      meta: {
        total: meta?.total ?? 0,
        limit: meta?.limit ?? 20,
        page: meta?.page ?? 1,
        total_pages: meta?.total_pages ?? 0,
      },
    };
  },

  getTenantStores: async (
    tenantId: string,
    filters: { page?: number; limit?: number; q?: string } = {},
  ): Promise<PaginatedListResponse<TenantStore>> => {
    const response = await apiClient.get<{ success: boolean; data: TenantStore[]; meta: PaginatedListResponse<TenantStore>['meta'] }>(
      `/tenants/${tenantId}/stores`,
      { params: filters },
    );
    const { data, meta } = response.data;
    return {
      items: data || [],
      meta: {
        total: meta?.total ?? 0,
        limit: meta?.limit ?? 20,
        page: meta?.page ?? 1,
        total_pages: meta?.total_pages ?? 0,
      },
    };
  },
};

export default tenantService;
